import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PLAN_LABELS } from "@/lib/mode";
import { Ticket, Copy, Plus, Prohibit, ArrowClockwise, Users, LinkSimple, EnvelopeSimple, ChatText, ChatCircleDots, ArrowBendUpLeft, Megaphone, Trash, Flask, RocketLaunch, ChartLineUp, CurrencyDollar, Receipt, Clock, Target, Sparkle, Eye, CursorClick, LockSimple, Broadcast, ShieldCheck } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "—";

const SENTIMENT_LABEL = {
  love: { label: "Loves it", color: "var(--bk-success)", bg: "var(--bk-success-bg)" },
  ok: { label: "It's okay", color: "#8A6A3B", bg: "#F6EEDF" },
  frustrated: { label: "Frustrated", color: "#B23B1F", bg: "#FDECE8" },
};

const badge = (active, uses, max) => {
  if (!active) return { label: "Revoked", bg: "#F3F0EC", color: "var(--bk-disabled)" };
  if (uses >= max) return { label: "Used up", bg: "#FDECE8", color: "#B23B1F" };
  return { label: "Available", bg: "var(--bk-success-bg)", color: "var(--bk-success)" };
};

const buildInvite = (signupUrl) => `Hi! It's Kamery, the founder of IronRoot.

Before I built IronRoot, I ran my own consulting business — and I was drowning in apps, documents, and questions I didn't know how to answer. I realized I was spending all my time managing my business instead of building it. So I built the business partner I wish I'd had: one AI that helps you start your business, keeps your books organized, stores your important documents, remembers your deadlines, answers your questions, and grows with you 24/7.

I'd love for you to be one of our first 10 Founding Members. As a thank-you for believing in this early, you'll lock in 40% off for life.

Use code FOUNDING40 at checkout: ${signupUrl}

Thank you for trusting IronRoot to be part of your journey — I can't wait to see what you build.

— Kamery Bradshaw, Founder, IronRoot`;

export default function Admin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [changelog, setChangelog] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [mission, setMission] = useState(null);
  const [clForm, setClForm] = useState({ title: "", body: "", tag: "New" });
  const [clSaving, setClSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [resetting, setResetting] = useState("");
  const [inviteCopied, setInviteCopied] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [settingUpStripe, setSettingUpStripe] = useState(false);
  const [ctaClicks, setCtaClicks] = useState({ summary: [], clicks: [] });
  const [auditLog, setAuditLog] = useState([]);
  const [marketPulse, setMarketPulse] = useState(null);
  const [brandAudit, setBrandAudit] = useState(null);
  const [runningAudit, setRunningAudit] = useState(false);

  const openPreview = async () => {
    setLoadingPreview(true);
    try {
      const { data } = await api.get("/admin/welcome-email-preview");
      setPreviewHtml(data.html);
    } catch {
      toast.error("Couldn't load the email preview.");
    } finally { setLoadingPreview(false); }
  };

  const sendTestEmail = async () => {
    if (!testEmail.trim()) { toast.error("Enter an email to send the test to."); return; }
    setSendingTest(true);
    try {
      await api.post("/admin/test-welcome-email", { email: testEmail.trim() });
      toast.success(`Welcome email sent to ${testEmail.trim()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the test email.");
    } finally { setSendingTest(false); }
  };

  const signupUrl = `${window.location.origin}/register`;
  const foundingInviteText = buildInvite(signupUrl);
  const copyInvite = () => {
    const done = () => { setInviteCopied(true); toast.success("Invite copied — paste it into a text, DM, or email"); setTimeout(() => setInviteCopied(false), 2500); };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(foundingInviteText).then(done).catch(() => fallbackCopy(foundingInviteText, done));
    } else {
      fallbackCopy(foundingInviteText, done);
    }
  };
  const fallbackCopy = (text, done) => {
    try {
      const ta = document.createElement("textarea");
      ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.focus(); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta); done();
    } catch { toast.error("Couldn't copy — select the text and copy manually."); }
  };

  const VERTICALS = [
    { key: "wellness", label: "Big Sky Wellness", desc: "Montana wellness clinic — bookings, supplies, memberships" },
    { key: "ecommerce", label: "Big Sky Gear", desc: "E-commerce store — inventory & online orders" },
    { key: "contractor", label: "Mountain Ridge Construction", desc: "Contractor — jobs, equipment rentals & insurance" },
    { key: "realestate", label: "Lakeview Rentals", desc: "Real estate rentals — Montana property management" },
    { key: "florida", label: "Sky Peak Realty", desc: "Florida real estate brokerage — no state income tax" },
  ];

  const resetVertical = async (v) => {
    if (!window.confirm(`This wipes and re-seeds the "${v.label}" practice business with fresh demo data. Continue?`)) return;
    setResetting(v.key);
    try {
      await api.post(`/admin/reset-practice/${v.key}`);
      toast.success(`${v.label} is fresh — demo data re-seeded.`);
    } catch {
      toast.error(`Couldn't reset ${v.label}. Please try again.`);
    } finally {
      setResetting("");
    }
  };

  const resetAll = async () => {
    if (!window.confirm("This wipes and re-seeds ALL practice businesses (and prunes any stale duplicates) so analytics stay accurate. Continue?")) return;
    setResetting("all");
    try {
      const { data } = await api.post("/admin/reset-practice");
      toast.success(`All practice businesses reset${data.pruned ? ` · pruned ${data.pruned} stale record${data.pruned === 1 ? "" : "s"}` : ""}.`);
    } catch {
      toast.error("Couldn't reset practice data. Please try again.");
    } finally {
      setResetting("");
    }
  };

  const purgeTests = async () => {
    if (!window.confirm("This permanently deletes every leftover automated-test business (names starting with TEST_) and all their data. Continue?")) return;
    setResetting("purge");
    try {
      const { data } = await api.post("/admin/purge-test-businesses");
      toast.success(data.purged ? `Purged ${data.purged} test business${data.purged === 1 ? "" : "es"} — analytics are clean now.` : "No leftover test businesses found.");
    } catch {
      toast.error("Couldn't purge test businesses. Please try again.");
    } finally {
      setResetting("");
    }
  };

  const seedDemo = async () => {
    setResetting("seed");
    try {
      const { data } = await api.post("/admin/seed-demo-forecast");
      toast.success(`Seeded ${data.created.length} demo businesses — open your dashboard to see healthy, tight & out-of-cash side by side.`);
    } catch {
      toast.error("Couldn't seed demo data. Please try again.");
    } finally {
      setResetting("");
    }
  };

  const clearDemo = async () => {
    if (!window.confirm("Remove all demo businesses (names starting with 'Demo —') and their data?")) return;
    setResetting("cleardemo");
    try {
      const { data } = await api.post("/admin/clear-demo-forecast");
      toast.success(data.cleared ? `Cleared ${data.cleared} demo business${data.cleared === 1 ? "" : "es"}.` : "No demo businesses to clear.");
    } catch {
      toast.error("Couldn't clear demo data. Please try again.");
    } finally {
      setResetting("");
    }
  };

  const load = () => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/invites").then((r) => setInvites(r.data)).catch(() => {});
    api.get("/admin/contacts").then((r) => setContacts(r.data)).catch(() => {});
    api.get("/admin/feedback").then((r) => setFeedback(r.data)).catch(() => {});
    api.get("/changelog").then((r) => setChangelog(r.data)).catch(() => {});
    api.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    api.get("/admin/mission-dashboard").then((r) => setMission(r.data)).catch(() => {});
    api.get("/admin/email-status").then((r) => setEmailStatus(r.data)).catch(() => {});
    api.get("/admin/stripe-status").then((r) => setStripeStatus(r.data)).catch(() => {});
    api.get("/admin/brand-audit").then((r) => setBrandAudit(r.data)).catch(() => {});
    api.get("/analytics/cta-clicks").then((r) => setCtaClicks(r.data)).catch(() => {});
    api.get("/audit-log").then((r) => setAuditLog(r.data.entries || [])).catch(() => {});
    api.get("/admin/market-pulse").then((r) => setMarketPulse(r.data)).catch(() => {});
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  const runStripeSetup = async () => {
    setSettingUpStripe(true);
    try {
      const { data } = await api.post("/admin/setup-stripe-catalog");
      toast.success(`Stripe catalog synced (${data.mode} mode)`);
      api.get("/admin/stripe-status").then((r) => setStripeStatus(r.data)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't set up the Stripe catalog.");
    } finally {
      setSettingUpStripe(false);
    }
  };

  const runBrandAudit = async () => {
    setRunningAudit(true);
    try {
      const { data } = await api.post("/admin/brand-audit/run");
      setBrandAudit(data);
      toast.success("Brand audit refreshed.");
    } catch {
      toast.error("Couldn't run the brand audit.");
    } finally {
      setRunningAudit(false);
    }
  };

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const createCodes = async (count) => {
    setCreating(true);
    try {
      await api.post("/admin/invites", { count, max_uses: 1, label: "Founding batch" });
      toast.success(`${count} invite code${count > 1 ? "s" : ""} created`);
      load();
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (code) => {
    await api.patch(`/admin/invites/${code}/revoke`);
    toast.success("Code revoked");
    load();
  };
  const activate = async (code) => {
    await api.patch(`/admin/invites/${code}/activate`);
    toast.success("Code reactivated");
    load();
  };

  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(label || "Copied"); }
    catch { toast.error("Copy failed — select manually"); }
  };

  const inviteLink = (code) => `${window.location.origin}/register?code=${code}`;

  const inviteMessage = (code) => buildInvite(inviteLink(code));

  const replyHref = (f) => {
    const subject = encodeURIComponent("Re: your IronRoot feedback");
    const snippet = f.message ? ` — "${f.message.slice(0, 120)}${f.message.length > 120 ? "…" : ""}"` : "";
    const body = encodeURIComponent(`Hi ${f.name || "there"},\n\nThank you so much for taking the time to share your feedback${snippet}.\n\n`);
    return `mailto:${f.email}?subject=${subject}&body=${body}`;
  };

  const addChangelog = async () => {
    if (!clForm.title.trim() || !clForm.body.trim()) return;
    setClSaving(true);
    try {
      await api.post("/admin/changelog", clForm);
      setClForm({ title: "", body: "", tag: "New" });
      toast.success("Update published to your What's New page");
      load();
    } catch {
      toast.error("Couldn't publish update");
    } finally {
      setClSaving(false);
    }
  };

  const deleteChangelog = async (id) => {
    try { await api.delete(`/admin/changelog/${id}`); load(); } catch {}
  };

  const availableCount = invites.filter((c) => c.active && c.uses < c.max_uses).length;
  const signupCount = users.filter((u) => u.role !== "admin").length;
  const totalInvites = invites.length;
  const usedInvites = invites.filter((c) => c.uses > 0).length;

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-6xl mx-auto px-6 lg:px-8 py-12">
        <span className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: "var(--bk-accent)" }}>Admin</span>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2 mb-8" style={{ color: "var(--bk-text)" }}>
          Beta control room
        </h1>

        {/* Mission Dashboard */}
        <section className="mb-10" data-testid="mission-dashboard">
          <div className="flex items-center gap-2.5 mb-4">
            <Target size={22} weight="duotone" color="var(--bk-accent)" />
            <h2 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Mission Dashboard</h2>
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: "var(--bk-page)", color: "var(--bk-text-2)" }}>Across all members</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bk-card p-6" data-testid="mission-revenue">
              <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--bk-text-2)" }}>
                <CurrencyDollar size={20} weight="duotone" color="var(--bk-success)" /> Total Revenue Invoiced
              </div>
              <p className="font-display text-3xl font-bold mt-3" style={{ color: "var(--bk-text)" }} data-testid="mission-revenue-value">
                {mission ? `$${Math.round(mission.revenue_invoiced).toLocaleString("en-US")}` : "—"}
              </p>
              <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{mission ? `${mission.invoices_count.toLocaleString("en-US")} invoices created` : "\u00a0"}</p>
            </div>
            <div className="bk-card p-6" data-testid="mission-deductions">
              <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--bk-text-2)" }}>
                <Receipt size={20} weight="duotone" color="var(--bk-accent)" /> Tax Deductions Found
              </div>
              <p className="font-display text-3xl font-bold mt-3" style={{ color: "var(--bk-text)" }} data-testid="mission-deductions-value">
                {mission ? `$${Math.round(mission.deductions_found).toLocaleString("en-US")}` : "—"}
              </p>
              <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{mission ? `${mission.deductions_count.toLocaleString("en-US")} categorized expenses` : "\u00a0"}</p>
            </div>
            <div className="bk-card p-6" data-testid="mission-hours">
              <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--bk-text-2)" }}>
                <Clock size={20} weight="duotone" color="var(--bk-energy)" /> Total Hours Saved
              </div>
              <p className="font-display text-3xl font-bold mt-3" style={{ color: "var(--bk-text)" }} data-testid="mission-hours-value">
                {mission ? `${mission.hours_saved.toLocaleString("en-US")} hrs` : "—"}
              </p>
              <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{mission ? `${mission.transactions_total.toLocaleString("en-US")} transactions × 15 min` : "\u00a0"}</p>
            </div>
          </div>
          {analytics && (
            <div className="bk-card p-5 mt-4" data-testid="mission-member-goal">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="font-semibold" style={{ color: "var(--bk-text)" }}>Journey to 100 members</span>
                <span style={{ color: "var(--bk-text-2)" }}><span className="font-bold" style={{ color: "var(--bk-accent)" }}>{analytics.paid_active}</span> / 100 paying members</span>
              </div>
              <div className="h-2.5 rounded-full overflow-hidden" style={{ background: "var(--bk-page)" }}>
                <div className="h-2.5 rounded-full" style={{ width: `${Math.min(100, (analytics.paid_active / 100) * 100)}%`, background: "var(--bk-gradient-primary)", transition: "width 0.5s ease" }} />
              </div>
            </div>
          )}
        </section>

        {/* Founding Member Invitation */}
        <section className="mb-10 bk-card p-6" data-testid="founding-invite">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start gap-2.5">
              <EnvelopeSimple size={24} weight="duotone" color="var(--bk-accent)" className="shrink-0 mt-0.5" />
              <div>
                <h2 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Founding Member Invitation</h2>
                <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>
                  Your personal invite — story + code + signup link, ready to paste into a text, DM, or email. Locks in <span className="font-semibold" style={{ color: "var(--bk-accent)" }}>40% off for life</span> for your first 10 members.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="inline-flex items-center gap-1.5 font-mono text-sm font-bold px-3 py-2 rounded-lg" style={{ background: "var(--bk-page)", color: "var(--bk-accent)" }} data-testid="founding-code">FOUNDING40</span>
              <button onClick={copyInvite} data-testid="copy-invite-btn" className="bk-btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold">
                <Copy size={16} weight="bold" /> {inviteCopied ? "Copied!" : "Copy Invite"}
              </button>
            </div>
          </div>
          <div className="mt-5 rounded-xl border p-4 text-sm leading-relaxed whitespace-pre-wrap" style={{ borderColor: "var(--bk-border)", background: "var(--bk-page)", color: "var(--bk-text-2)", maxHeight: 260, overflowY: "auto" }} data-testid="invite-preview">
            {foundingInviteText}
          </div>
        </section>

        {/* Automated Welcome Email */}
        <section className="mb-10 bk-card p-6" data-testid="welcome-email">
          <div className="flex items-start gap-2.5">
            <EnvelopeSimple size={24} weight="duotone" color="var(--bk-accent)" className="shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Automated Welcome Email</h2>
                <span
                  data-testid="email-config-badge"
                  className="text-xs font-bold px-2.5 py-1 rounded-full"
                  style={emailStatus?.configured
                    ? { background: "var(--bk-success-bg)", color: "var(--bk-success)" }
                    : { background: "#FDECE8", color: "#B23B1F" }}
                >
                  {emailStatus?.configured ? "Live" : "Not configured"}
                </span>
              </div>
              <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>
                Every new signup automatically gets your Founder welcome letter with the <span className="font-semibold" style={{ color: "var(--bk-accent)" }}>FOUNDING40</span> code.
                {emailStatus?.configured
                  ? <> Sending from <span className="font-mono">{emailStatus?.sender}</span>.</>
                  : <> Add a Resend API key &amp; verified sender to the backend to turn this on.</>}
              </p>
              <div className="mt-4 flex flex-wrap items-end gap-2">
                <div className="flex-1 min-w-[220px]">
                  <label className="text-xs font-semibold" style={{ color: "var(--bk-text-2)" }}>Send a test to</label>
                  <input
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="you@example.com"
                    data-testid="test-email-input"
                    disabled={!emailStatus?.configured}
                    className="mt-1 w-full px-3 py-2 rounded-lg border text-sm disabled:opacity-50"
                    style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}
                  />
                </div>
                <button
                  onClick={openPreview}
                  disabled={loadingPreview}
                  data-testid="preview-email-btn"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50"
                  style={{ border: "1px solid var(--bk-border)", color: "var(--bk-text)" }}
                >
                  <Eye size={16} weight="bold" /> {loadingPreview ? "Loading…" : "Preview welcome email"}
                </button>
                <button
                  onClick={sendTestEmail}
                  disabled={!emailStatus?.configured || sendingTest}
                  data-testid="send-test-email-btn"
                  className="bk-btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50"
                >
                  <EnvelopeSimple size={16} weight="bold" /> {sendingTest ? "Sending…" : "Send test"}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Stripe / Payments */}
        <section className="mb-10 bk-card p-6" data-testid="stripe-setup">
          <div className="flex items-start gap-2.5">
            <CurrencyDollar size={24} weight="duotone" color="var(--bk-accent)" className="shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Stripe payments</h2>
                {stripeStatus && (
                  <span
                    data-testid="stripe-mode-badge"
                    className="text-xs font-bold px-2.5 py-1 rounded-full"
                    style={stripeStatus.mode === "live"
                      ? { background: "var(--bk-success-bg)", color: "var(--bk-success)" }
                      : { background: "#F6EEDF", color: "#8A6A3B" }}
                  >
                    {stripeStatus.mode === "live" ? "LIVE mode" : "TEST mode"}
                  </span>
                )}
                {stripeStatus && (
                  <span data-testid="stripe-founding-badge" className="text-xs font-semibold px-2.5 py-1 rounded-full"
                    style={stripeStatus.founding_code_active
                      ? { background: "var(--bk-success-bg)", color: "var(--bk-success)" }
                      : { background: "#FDECE8", color: "#B23B1F" }}>
                    FOUNDING40 {stripeStatus.founding_code_active ? "active" : "missing"}
                  </span>
                )}
              </div>
              <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>
                After you deploy with your <span className="font-semibold">live</span> Stripe keys, run this once to create your live
                plans (Starter/Professional/Business) and the <span className="font-semibold" style={{ color: "var(--bk-accent)" }}>FOUNDING40</span> discount.
                It's safe to run again anytime — it only fills in anything missing.
              </p>
              {stripeStatus?.catalog && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="stripe-catalog">
                  {Object.entries(stripeStatus.catalog).map(([lk, amt]) => (
                    <span key={lk} className="text-xs font-mono px-2.5 py-1 rounded-full"
                      style={{ background: "var(--bk-page)", color: amt ? "var(--bk-text)" : "#B23B1F" }}>
                      {lk.replace("_monthly", "")}: {amt ? `$${(amt / 100).toFixed(0)}/mo` : "missing"}
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-4">
                <button
                  onClick={runStripeSetup}
                  disabled={settingUpStripe}
                  data-testid="setup-stripe-btn"
                  className="bk-btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50"
                >
                  <CurrencyDollar size={16} weight="bold" /> {settingUpStripe ? "Syncing…" : "Set up Stripe catalog"}
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="mb-10 bk-card p-6" data-testid="brand-audit-panel">
          <div className="flex items-start gap-2.5">
            <ShieldCheck size={24} weight="duotone" color="var(--bk-success)" className="shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Brand Audit</h2>
                  <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>
                    Checks live sender/contact emails, IronRoot page titles, logo assets, landing copy, and email-template branding. Stripe product titles still need a manual dashboard review.
                  </p>
                </div>
                <button onClick={runBrandAudit} disabled={runningAudit} data-testid="run-brand-audit-btn" className="bk-btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50">
                  <ArrowClockwise size={16} weight="bold" className={runningAudit ? "animate-spin" : ""} /> {runningAudit ? "Running audit…" : "Run Audit"}
                </button>
              </div>

              <div className="mt-4 rounded-2xl border overflow-hidden" style={{ borderColor: "var(--bk-border)" }}>
                <div className="grid grid-cols-1 md:grid-cols-[1.3fr_120px_1fr] gap-3 px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em]" style={{ background: "var(--bk-page)", color: "var(--bk-text-2)" }}>
                  <span>Check</span>
                  <span>Status</span>
                  <span>Last checked</span>
                </div>
                <div className="divide-y" style={{ borderColor: "var(--bk-border)" }}>
                  {(brandAudit?.checks || []).map((check) => (
                    <div key={check.key} className="px-4 py-4 grid grid-cols-1 md:grid-cols-[1.3fr_120px_1fr] gap-3" data-testid={`brand-audit-${check.key}`}>
                      <div>
                        <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{check.label}</p>
                        <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{check.detail}</p>
                      </div>
                      <div className="text-sm font-semibold" style={{ color: check.passed ? "var(--bk-success)" : "#B23B1F" }} data-testid={`brand-audit-status-${check.key}`}>
                        {check.passed ? "✅ Pass" : "❌ Fail"}
                      </div>
                      <div className="text-xs" style={{ color: "var(--bk-text-2)" }}>{fmtDateTime(check.last_checked_at)}</div>
                    </div>
                  ))}
                  {!brandAudit?.checks?.length && (
                    <div className="px-4 py-6 text-sm" style={{ color: "var(--bk-text-2)" }} data-testid="brand-audit-empty">No audit snapshot yet.</div>
                  )}
                </div>
              </div>

              <p className="text-xs mt-3" style={{ color: "var(--bk-text-2)" }} data-testid="brand-audit-last-checked">
                Last checked: {fmtDateTime(brandAudit?.last_checked_at)}
              </p>
            </div>
          </div>
        </section>

        {/* Demo data (for live walkthroughs) */}
        <section className="mb-10 bk-card p-6" data-testid="demo-data">
          <div className="flex items-start gap-2.5">
            <Sparkle size={24} weight="duotone" color="var(--bk-accent)" className="shrink-0 mt-0.5" />
            <div className="flex-1">
              <h2 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Demo forecast data</h2>
              <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>
                Instantly spin up three sample businesses — a <span className="font-semibold" style={{ color: "var(--bk-success)" }}>healthy</span>,
                a <span className="font-semibold" style={{ color: "#B45309" }}>tight</span>, and an
                <span className="font-semibold" style={{ color: "var(--bk-energy)" }}> out-of-cash</span> one — so your walkthrough shows every runway state side by side. Clear them in one tap when you're done.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={seedDemo} disabled={resetting === "seed"} data-testid="seed-demo-btn"
                  className="bk-btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50">
                  <Sparkle size={16} weight="bold" /> {resetting === "seed" ? "Seeding…" : "Seed demo businesses"}
                </button>
                <button onClick={clearDemo} disabled={resetting === "cleardemo"} data-testid="clear-demo-btn"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50"
                  style={{ color: "var(--bk-text-2)", border: "1px solid var(--bk-border)" }}>
                  <Trash size={16} weight="bold" /> {resetting === "cleardemo" ? "Clearing…" : "Clear demo data"}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Launch analytics */}
        <section className="mb-10" data-testid="admin-analytics">
          <div className="flex items-center gap-2.5 mb-4">
            <RocketLaunch size={22} weight="duotone" color="var(--bk-accent)" />
            <h2 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Launch analytics</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="rounded-2xl p-5 text-white sm:col-span-1 col-span-2" style={{ background: "var(--bk-gradient-primary)" }} data-testid="analytics-mrr">
              <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: "rgba(255,255,255,0.85)" }}>MRR</p>
              <p className="font-mono text-3xl font-bold mt-1">${(analytics?.mrr ?? 0).toFixed(2)}</p>
              <p className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.85)" }}>{analytics?.active_members ?? 0} active member{(analytics?.active_members ?? 0) === 1 ? "" : "s"}</p>
            </div>
            {[
              { label: "Trials active", value: analytics?.trials_active ?? 0, testid: "analytics-trials" },
              { label: "Paid users", value: analytics?.paid_active ?? 0, testid: "analytics-paid" },
              { label: "Signups (7d)", value: analytics?.signups_7d ?? 0, testid: "analytics-signups7d" },
              { label: "Membership offers", value: analytics?.membership_offers ?? 0, testid: "analytics-offers" },
              { label: "Members sold", value: analytics?.active_members ?? 0, testid: "analytics-members" },
              { label: "Businesses", value: analytics?.businesses_total ?? 0, testid: "analytics-businesses" },
              { label: "Invoice revenue", value: `$${(analytics?.invoice_revenue ?? 0).toFixed(0)}`, testid: "analytics-revenue" },
            ].map((s) => (
              <div key={s.label} data-testid={s.testid} className="bk-card p-5">
                <p className="font-display text-2xl font-bold leading-none" style={{ color: "var(--bk-text)" }}>{s.value}</p>
                <p className="text-xs mt-1.5 uppercase tracking-[0.12em] font-semibold" style={{ color: "var(--bk-text-2)" }}>{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-10 bk-card p-6" data-testid="admin-conversions-card">
          <div className="flex items-center gap-2.5 mb-4">
            <CursorClick size={22} weight="duotone" color="var(--bk-accent)" />
            <h2 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Conversions</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-6">
            <div className="overflow-hidden rounded-2xl border" style={{ borderColor: "var(--bk-border)" }}>
              <div className="grid grid-cols-[120px_1fr_110px_180px] gap-3 px-4 py-3 text-xs font-semibold uppercase tracking-[0.12em]" style={{ background: "var(--bk-page)", color: "var(--bk-text-2)" }}>
                <span>Plan</span><span>Source page</span><span className="text-right">Clicks</span><span>Latest</span>
              </div>
              <div className="divide-y" style={{ borderColor: "var(--bk-border)" }}>
                {(ctaClicks.summary || []).length === 0 ? (
                  <div className="px-4 py-8 text-sm text-center" style={{ color: "var(--bk-text-2)" }}>No Stripe CTA clicks yet.</div>
                ) : (ctaClicks.summary || []).map((row, idx) => (
                  <div key={`${row.plan_name}-${row.source_page}-${idx}`} className="grid grid-cols-[120px_1fr_110px_180px] gap-3 px-4 py-3 text-sm" data-testid={`conversion-row-${idx}`}>
                    <span className="font-semibold" style={{ color: "var(--bk-text)" }}>{row.plan_name}</span>
                    <span style={{ color: "var(--bk-text-2)" }}>{row.source_page}</span>
                    <span className="text-right font-semibold" style={{ color: "var(--bk-accent)" }}>{row.total_count}</span>
                    <span style={{ color: "var(--bk-text-2)" }}>{fmtDateTime(row.most_recent_clicked_at)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border p-4" style={{ borderColor: "var(--bk-border)", background: "linear-gradient(180deg, rgba(255,249,240,0.7), #fff)" }}>
              <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-accent)" }}>Recent clicks</p>
              <div className="space-y-3 mt-4 max-h-[320px] overflow-y-auto">
                {(ctaClicks.clicks || []).slice(0, 10).map((click) => (
                  <div key={click.id} className="rounded-xl px-3 py-3" style={{ background: "var(--bk-page)" }} data-testid={`conversion-click-${click.id}`}>
                    <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{click.source_element}</p>
                    <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{click.plan_name} · {click.source_page}</p>
                    <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{fmtDateTime(click.clicked_at)}{click.user_id ? ` · user ${click.user_id.slice(0, 8)}…` : " · guest"}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="mb-10 bk-card p-6" data-testid="admin-market-pulse-card">
          <div className="flex items-start gap-2.5 mb-5">
            <Broadcast size={24} weight="duotone" color="var(--bk-success)" className="shrink-0 mt-0.5" />
            <div>
              <h2 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Market Pulse foundation</h2>
              <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>Anonymous trend snapshots are accumulating here. Local insights need 10+ opted-in businesses per cohort before Rowan can speak confidently.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
            <div className="bk-card p-5" data-testid="market-pulse-total-opted-in">
              <p className="text-xs uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Opted-in users</p>
              <p className="font-display text-3xl font-bold mt-2" style={{ color: "var(--bk-text)" }}>{marketPulse?.total_opted_in_users ?? 0}</p>
            </div>
            <div className="bk-card p-5" data-testid="market-pulse-total-snapshots">
              <p className="text-xs uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Snapshots stored</p>
              <p className="font-display text-3xl font-bold mt-2" style={{ color: "var(--bk-text)" }}>{marketPulse?.total_snapshots ?? 0}</p>
            </div>
            <div className="bk-card p-5" data-testid="market-pulse-ready-cohorts">
              <p className="text-xs uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Local-ready cohorts</p>
              <p className="font-display text-3xl font-bold mt-2" style={{ color: "var(--bk-text)" }}>{marketPulse?.ready_cohort_count ?? 0}</p>
              <p className="text-xs mt-2" style={{ color: "var(--bk-text-2)" }}>Market Pulse needs 10+ businesses in your area for local insights. Currently: {marketPulse?.cohorts?.[0]?.current_count ?? 0}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--bk-border)" }}>
              <div className="px-4 py-3 text-xs uppercase tracking-[0.14em] font-semibold" style={{ background: "var(--bk-page)", color: "var(--bk-text-2)" }}>Opted-in users by state</div>
              <div className="divide-y" style={{ borderColor: "var(--bk-border)" }}>
                {(marketPulse?.by_state || []).slice(0, 8).map((row) => (
                  <div key={row.state} className="px-4 py-3 flex items-center justify-between text-sm" data-testid={`market-pulse-state-${row.state}`}>
                    <span style={{ color: "var(--bk-text)" }}>{row.state}</span>
                    <span className="font-semibold" style={{ color: "var(--bk-accent)" }}>{row.count}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--bk-border)" }}>
              <div className="px-4 py-3 text-xs uppercase tracking-[0.14em] font-semibold" style={{ background: "var(--bk-page)", color: "var(--bk-text-2)" }}>Opted-in users by industry</div>
              <div className="divide-y" style={{ borderColor: "var(--bk-border)" }}>
                {(marketPulse?.by_industry || []).slice(0, 8).map((row) => (
                  <div key={row.industry} className="px-4 py-3 flex items-center justify-between text-sm" data-testid={`market-pulse-industry-${row.industry.replace(/\s+/g, '-').toLowerCase()}`}>
                    <span style={{ color: "var(--bk-text)" }}>{row.industry}</span>
                    <span className="font-semibold" style={{ color: "var(--bk-accent)" }}>{row.count}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--bk-border)" }}>
              <div className="px-4 py-3 text-xs uppercase tracking-[0.14em] font-semibold" style={{ background: "var(--bk-page)", color: "var(--bk-text-2)" }}>Cohort data quality</div>
              <div className="divide-y" style={{ borderColor: "var(--bk-border)" }}>
                {(marketPulse?.cohorts || []).slice(0, 6).map((row, idx) => (
                  <div key={`${row.state}-${row.industry}-${idx}`} className="px-4 py-3 text-sm" data-testid={`market-pulse-cohort-${idx}`}>
                    <p style={{ color: "var(--bk-text)" }}>{row.industry} · {row.county ? `${row.county}, ` : ""}{row.state}</p>
                    <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>Market Pulse needs 10+ businesses in your area for local insights. Currently: {row.current_count}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="mb-10 bk-card p-6" data-testid="admin-audit-log-card">
          <div className="flex items-center gap-2.5 mb-4">
            <LockSimple size={22} weight="duotone" color="#FF6B35" />
            <h2 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Audit log</h2>
          </div>
          <div className="space-y-3">
            {auditLog.length === 0 ? (
              <div className="rounded-xl p-4 text-sm" style={{ background: "var(--bk-page)", color: "var(--bk-text-2)" }}>No audit events yet.</div>
            ) : auditLog.slice(0, 12).map((entry) => (
              <div key={entry.id} className="rounded-xl p-4 flex flex-wrap items-center justify-between gap-3" style={{ background: "var(--bk-page)" }} data-testid={`audit-log-${entry.id}`}>
                <div>
                  <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{entry.summary || entry.period_label || entry.invoice_number || entry.action}</p>
                  <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>
                    {entry.action === "invoice_reminder_sent"
                      ? `Reminder email · ${entry.recipient_email || entry.client_name || "recipient"}`
                      : `Closed by ${entry.closed_by_email || entry.closed_by_user_id}`}
                  </p>
                </div>
                <span className="text-xs" style={{ color: "var(--bk-text-2)" }}>{fmtDateTime(entry.created_at || entry.closed_at)}</span>
              </div>
            ))}
          </div>
        </section>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
          {[
            { label: "Invites created", value: totalInvites, testid: "stat-invites-created" },
            { label: "Codes redeemed", value: usedInvites, testid: "stat-codes-redeemed" },
            { label: "Signups", value: signupCount, testid: "stat-signups" },
            { label: "Invite requests", value: contacts.length, testid: "stat-requests" },
          ].map((s) => (
            <div key={s.label} data-testid={s.testid} className="bk-card p-5">
              <p className="font-display text-3xl font-bold leading-none" style={{ color: "var(--bk-text)" }}>{s.value}</p>
              <p className="text-xs mt-1.5 uppercase tracking-[0.12em] font-semibold" style={{ color: "var(--bk-text-2)" }}>{s.label}</p>
            </div>
          ))}
        </div>
        <p className="text-sm -mt-4 mb-8" style={{ color: "var(--bk-text-2)" }} data-testid="conversion-line">
          {totalInvites > 0
            ? `${signupCount} of ${totalInvites} invite${totalInvites === 1 ? "" : "s"} turned into signups${totalInvites ? ` · ${Math.round((signupCount / totalInvites) * 100)}% conversion` : ""}.`
            : "Generate invite codes below to start inviting bookkeepers."}
        </p>

        {/* Practice data */}
        <section className="bk-card p-6 mb-8" data-testid="admin-practice">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
            <div className="flex items-start gap-2.5">
              <Flask size={24} weight="duotone" color="var(--bk-accent)" className="shrink-0 mt-0.5" />
              <div>
                <h2 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Practice data</h2>
                <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>Reset a single demo vertical with fresh sample data, or reset them all (prunes stale duplicates so analytics stay accurate).</p>
              </div>
            </div>
            <button onClick={resetAll} disabled={!!resetting} data-testid="reset-all-practice-btn" className="bk-btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-semibold shrink-0 disabled:opacity-70">
              <ArrowClockwise size={16} weight="bold" className={resetting === "all" ? "animate-spin" : ""} /> {resetting === "all" ? "Resetting all…" : "Reset ALL practice businesses"}
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {VERTICALS.map((v) => (
              <div key={v.key} data-testid={`practice-card-${v.key}`} className="rounded-xl border p-4 flex flex-col gap-3" style={{ borderColor: "var(--bk-border)", background: "var(--bk-page)" }}>
                <div>
                  <p className="font-display font-bold" style={{ color: "var(--bk-text)" }}>{v.label}</p>
                  <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{v.desc}</p>
                </div>
                <button onClick={() => resetVertical(v)} disabled={!!resetting} data-testid={`reset-practice-${v.key}`} className="mt-auto inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold border hover:bg-white disabled:opacity-70" style={{ borderColor: "var(--bk-border)", color: "var(--bk-accent)" }}>
                  <ArrowClockwise size={14} weight="bold" className={resetting === v.key ? "animate-spin" : ""} /> {resetting === v.key ? "Resetting…" : "Reset this one"}
                </button>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t flex flex-wrap items-center justify-between gap-3" style={{ borderColor: "var(--bk-border)" }}>
            <p className="text-xs" style={{ color: "var(--bk-text-2)" }}>Clean up after QA: remove every leftover <span className="font-mono">TEST_*</span> business so your Launch analytics read true.</p>
            <button onClick={purgeTests} disabled={!!resetting} data-testid="purge-tests-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold border shrink-0 disabled:opacity-70 hover:bg-[#FDECE8]" style={{ borderColor: "#B23B1F", color: "#B23B1F" }}>
              <Trash size={16} weight="bold" className={resetting === "purge" ? "animate-pulse" : ""} /> {resetting === "purge" ? "Purging…" : "Purge test businesses"}
            </button>
          </div>
        </section>

        {/* Invite codes */}
        <section className="bk-card p-6 mb-8" data-testid="admin-invites">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <div className="flex items-center gap-2.5">
              <Ticket size={24} weight="duotone" color="var(--bk-accent)" />
              <h2 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>
                Invite codes <span className="text-sm font-normal" style={{ color: "var(--bk-text-2)" }}>· {availableCount} available</span>
              </h2>
            </div>
            <div className="flex gap-2">
              <button onClick={() => createCodes(1)} disabled={creating} data-testid="create-1-code" className="px-4 py-2 rounded-md text-sm font-semibold border inline-flex items-center gap-1.5 hover:bg-[#F5F5F0]" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                <Plus size={16} weight="bold" /> 1
              </button>
              <button onClick={() => createCodes(5)} disabled={creating} data-testid="create-5-codes" className="bk-btn-primary px-4 py-2 rounded-md text-sm font-semibold inline-flex items-center gap-1.5">
                <Plus size={16} weight="bold" /> Generate 5 codes
              </button>
            </div>
          </div>

          <div className="mb-5 rounded-md border p-4" style={{ borderColor: "var(--bk-border)", background: "var(--bk-page)" }} data-testid="invite-message-note">
            <p className="text-xs" style={{ color: "var(--bk-text-2)" }}>
              Every code's <span className="font-semibold">Copy message</span> button sends your personal Founder story with that code's join link and the <span className="font-mono px-1 rounded" style={{ background: "#fff" }}>FOUNDING40</span> offer baked in — ready to paste into a text, DM, or email.
            </p>
          </div>

          {invites.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>No codes yet — generate some to invite bookkeepers.</p>
          ) : (
            <div className="space-y-2">
              {invites.map((c) => {
                const b = badge(c.active, c.uses, c.max_uses);
                return (
                  <div key={c.code} data-testid={`invite-${c.code}`} className="flex flex-wrap items-center gap-3 py-2.5 px-3 rounded-md" style={{ background: "var(--bk-page)" }}>
                    <span className="font-mono text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{c.code}</span>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: b.bg, color: b.color }}>{b.label}</span>
                    <span className="text-xs" style={{ color: "var(--bk-text-2)" }}>{c.uses}/{c.max_uses} used</span>
                    <div className="ml-auto flex items-center gap-1">
                      <button onClick={() => copy(inviteMessage(c.code), "Full invite message copied — paste into email or text")} data-testid={`copy-msg-${c.code}`} className="px-2.5 py-1 rounded text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-white" style={{ color: "var(--bk-accent)" }} title="Copy the full ready-to-send invite message">
                        <ChatText size={15} weight="bold" /> Copy message
                      </button>
                      <button onClick={() => copy(inviteLink(c.code), "Invite link copied")} data-testid={`copy-link-${c.code}`} className="px-2.5 py-1 rounded text-xs font-semibold inline-flex items-center gap-1.5 hover:bg-white" style={{ color: "var(--bk-accent)" }} title="Copy click-to-join link">
                        <LinkSimple size={15} weight="bold" /> Copy link
                      </button>
                      <button onClick={() => copy(c.code, "Code copied")} data-testid={`copy-${c.code}`} className="p-1.5 rounded hover:bg-white" title="Copy code only">
                        <Copy size={16} weight="duotone" color="var(--bk-text-2)" />
                      </button>
                      {c.active ? (
                        <button onClick={() => revoke(c.code)} data-testid={`revoke-${c.code}`} className="p-1.5 rounded hover:bg-white" title="Revoke">
                          <Prohibit size={16} weight="duotone" color="#B23B1F" />
                        </button>
                      ) : (
                        <button onClick={() => activate(c.code)} className="p-1.5 rounded hover:bg-white" title="Reactivate">
                          <ArrowClockwise size={16} weight="duotone" color="var(--bk-success)" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Invite requests */}
        <section className="bk-card p-6 mb-8" data-testid="admin-contacts">
          <div className="flex items-center gap-2.5 mb-5">
            <EnvelopeSimple size={24} weight="duotone" color="var(--bk-accent)" />
            <h2 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>
              Invite requests <span className="text-sm font-normal" style={{ color: "var(--bk-text-2)" }}>· {contacts.length}</span>
            </h2>
          </div>
          {contacts.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>No requests yet. People who submit the landing-page form show up here.</p>
          ) : (
            <div className="space-y-3">
              {contacts.map((c, i) => (
                <div key={i} data-testid={`contact-row-${i}`} className="p-4 rounded-md" style={{ background: "var(--bk-page)" }}>
                  <div className="flex flex-wrap items-center gap-2 justify-between">
                    <div>
                      <span className="font-semibold" style={{ color: "var(--bk-text)" }}>{c.name}</span>
                      <a href={`mailto:${c.email}`} className="ml-2 text-sm" style={{ color: "var(--bk-accent)" }}>{c.email}</a>
                    </div>
                    <span className="text-xs" style={{ color: "var(--bk-text-2)" }}>{c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}</span>
                  </div>
                  {c.message && <p className="text-sm mt-2" style={{ color: "var(--bk-text-2)" }}>{c.message}</p>}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Feedback */}
        <section className="bk-card p-6 mb-8" data-testid="admin-feedback">
          <div className="flex items-center gap-2.5 mb-5">
            <ChatCircleDots size={24} weight="duotone" color="var(--bk-accent)" />
            <h2 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>
              User feedback <span className="text-sm font-normal" style={{ color: "var(--bk-text-2)" }}>· {feedback.length}</span>
            </h2>
          </div>
          {feedback.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>No feedback yet. Users can send thoughts via the Feedback button in the app.</p>
          ) : (
            <div className="space-y-3">
              {feedback.map((f, i) => {
                const s = SENTIMENT_LABEL[f.sentiment];
                return (
                  <div key={i} data-testid={`feedback-row-${i}`} className="p-4 rounded-md" style={{ background: "var(--bk-page)" }}>
                    <div className="flex flex-wrap items-center gap-2 justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold" style={{ color: "var(--bk-text)" }}>{f.name || "User"}</span>
                        <a href={`mailto:${f.email}`} className="text-sm" style={{ color: "var(--bk-accent)" }}>{f.email}</a>
                        {s && <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: s.bg, color: s.color }}>{s.label}</span>}
                      </div>
                      <div className="flex items-center gap-3">
                        <a href={replyHref(f)} data-testid={`feedback-reply-${i}`} className="text-xs font-semibold inline-flex items-center gap-1 hover:underline" style={{ color: "var(--bk-accent)" }}>
                          <ArrowBendUpLeft size={14} weight="bold" /> Reply
                        </a>
                        <span className="text-xs" style={{ color: "var(--bk-text-2)" }}>{f.created_at ? new Date(f.created_at).toLocaleDateString() : ""}</span>
                      </div>
                    </div>
                    <p className="text-sm mt-2" style={{ color: "var(--bk-text-2)" }}>{f.message}</p>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* What's New management */}
        <section className="bk-card p-6 mb-8" data-testid="admin-changelog">
          <div className="flex items-center gap-2.5 mb-5">
            <Megaphone size={24} weight="duotone" color="var(--bk-accent)" />
            <h2 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>
              What's New <span className="text-sm font-normal" style={{ color: "var(--bk-text-2)" }}>· published to your public page</span>
            </h2>
          </div>
          <div className="space-y-3 mb-6">
            <div className="flex gap-2">
              {["New", "Improved", "Fixed"].map((t) => (
                <button key={t} type="button" onClick={() => setClForm((f) => ({ ...f, tag: t }))} data-testid={`cl-tag-${t}`} className="px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors" style={{ borderColor: clForm.tag === t ? "var(--bk-accent)" : "var(--bk-border)", color: clForm.tag === t ? "var(--bk-accent)" : "var(--bk-text-2)", background: clForm.tag === t ? "var(--bk-page)" : "#fff" }}>{t}</button>
              ))}
            </div>
            <input data-testid="cl-title" value={clForm.title} onChange={(e) => setClForm((f) => ({ ...f, title: e.target.value }))} placeholder="Title (e.g., Added a Feedback button)" className="w-full px-4 py-2.5 rounded-md border bg-white text-sm outline-none focus:ring-2" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
            <textarea data-testid="cl-body" value={clForm.body} onChange={(e) => setClForm((f) => ({ ...f, body: e.target.value }))} rows={3} placeholder="What changed and why it helps your users…" className="w-full px-4 py-2.5 rounded-md border bg-white text-sm outline-none focus:ring-2 leading-relaxed" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
            <button onClick={addChangelog} disabled={clSaving || !clForm.title.trim() || !clForm.body.trim()} data-testid="cl-publish" className="bk-btn-primary px-4 py-2 rounded-md text-sm font-semibold disabled:opacity-60">{clSaving ? "Publishing…" : "Publish update"}</button>
          </div>
          {changelog.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>No updates published yet. Publish your first one — your users see it on the public What's New page.</p>
          ) : (
            <div className="space-y-2">
              {changelog.map((c) => (
                <div key={c.id} data-testid={`cl-row-${c.id}`} className="flex items-start gap-3 p-3 rounded-md" style={{ background: "var(--bk-page)" }}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold" style={{ color: "var(--bk-accent)" }}>{c.tag}</span>
                      <span className="font-semibold text-sm" style={{ color: "var(--bk-text)" }}>{c.title}</span>
                    </div>
                    <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{c.body}</p>
                  </div>
                  <button onClick={() => deleteChangelog(c.id)} data-testid={`cl-delete-${c.id}`} className="p-1.5 rounded hover:bg-white shrink-0" style={{ color: "#B23B1F" }} aria-label="Delete update"><Trash size={16} weight="bold" /></button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Signups */}
        <section className="bk-card p-6" data-testid="admin-users">
          <div className="flex items-center gap-2.5 mb-5">
            <Users size={24} weight="duotone" color="var(--bk-success)" />
            <h2 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>
              Signups <span className="text-sm font-normal" style={{ color: "var(--bk-text-2)" }}>· {users.length}</span>
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left" style={{ color: "var(--bk-text-2)" }}>
                  <th className="pb-3 pr-4 font-semibold">Name</th>
                  <th className="pb-3 pr-4 font-semibold">Email</th>
                  <th className="pb-3 pr-4 font-semibold">Plan</th>
                  <th className="pb-3 pr-4 font-semibold">Clients</th>
                  <th className="pb-3 pr-4 font-semibold">Invite</th>
                  <th className="pb-3 font-semibold">Joined</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.email}`} className="border-t" style={{ borderColor: "var(--bk-border)" }}>
                    <td className="py-3 pr-4" style={{ color: "var(--bk-text)" }}>{u.name || "—"} {u.role === "admin" && <span className="text-xs" style={{ color: "var(--bk-accent)" }}>(admin)</span>}</td>
                    <td className="py-3 pr-4" style={{ color: "var(--bk-text-2)" }}>{u.email}</td>
                    <td className="py-3 pr-4"><span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: "var(--bk-success-bg)", color: "var(--bk-success)" }}>{PLAN_LABELS[u.plan] || u.plan}</span></td>
                    <td className="py-3 pr-4" style={{ color: "var(--bk-text-2)" }}>{u.businesses}</td>
                    <td className="py-3 pr-4 font-mono text-xs" style={{ color: "var(--bk-text-2)" }}>{u.invite_code || "—"}</td>
                    <td className="py-3 text-xs" style={{ color: "var(--bk-text-2)" }}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      <Dialog open={!!previewHtml} onOpenChange={(o) => !o && setPreviewHtml(null)}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden" data-testid="email-preview-modal">
          <DialogHeader className="px-5 pt-5">
            <DialogTitle className="font-display">Welcome email preview</DialogTitle>
          </DialogHeader>
          <p className="px-5 text-xs" style={{ color: "var(--bk-text-2)" }}>
            This is exactly what a new member receives from <span className="font-mono">{emailStatus?.sender}</span>.
          </p>
          <iframe
            title="Welcome email preview"
            srcDoc={previewHtml || ""}
            data-testid="email-preview-iframe"
            className="w-full mt-3"
            style={{ height: "60vh", border: "none", background: "#F8FAFC" }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
